import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/theme';

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const CarouselWrapper = styled.div<{ $isGrid?: boolean }>`
  position: relative;
  width: 100%;
  overflow: ${props => props.$isGrid ? 'visible' : 'hidden'};
  margin: 10px 0;
  background: ${props => props.$isGrid ? 'transparent' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 12px;
  padding: ${props => props.$isGrid ? '0' : '8px 0'};
  border: ${props => props.$isGrid ? 'none' : '1px solid rgba(255, 215, 0, 0.15)'};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 8px 0;
    padding: 6px 0;
    border-radius: 10px;
  }
`;

const GridWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 10px 0;
  
  /* Hide scrollbar but keep functionality */
  &::-webkit-scrollbar {
    height: 0;
  }
  
  /* Ensure all cards stay in one row */
  > div {
    flex: 0 0 auto;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, auto);
    
    > div:nth-child(1) { grid-column: 1; grid-row: 1; }
    > div:nth-child(2) { grid-column: 2; grid-row: 1; }
    > div:nth-child(3) { grid-column: 1; grid-row: 2; }
    > div:nth-child(4) { grid-column: 2; grid-row: 2; }
    > div:nth-child(5) { grid-column: 1 / 3; grid-row: 3; margin: 0 auto; }
  }
`;

const CarouselTrack = styled.div<{ $isPaused: boolean }>`
  display: flex;
  gap: 12px;
  animation: ${scroll} 35s linear infinite;
  animation-play-state: ${props => props.$isPaused ? 'paused' : 'running'};
  
  &:hover {
    animation-play-state: paused;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 8px;
    animation-duration: 25s;
  }
`;

const TestimonialCard = styled.div<{ $isGrid?: boolean }>`
  flex: 0 0 auto;
  width: ${props => props.$isGrid ? '180px' : '140px'};
  max-width: ${props => props.$isGrid ? '180px' : '140px'};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%);
  border: 1px solid rgba(255, 215, 0, 0.25);
  border-radius: 10px;
  padding: 10px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 10px 25px rgba(255, 215, 0, 0.15);
    border-color: rgba(255, 215, 0, 0.4);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: ${props => props.$isGrid ? '120px' : '110px'};
    max-width: ${props => props.$isGrid ? '120px' : '110px'};
    padding: 8px;
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  height: 70px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #2c3e50 0%, #1a2332 100%);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 55px;
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  background: rgba(255, 215, 0, 0.85);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &::after {
    content: '▶';
    color: #1a1a1a;
    font-size: 12px;
    margin-left: 2px;
  }
  
  ${TestimonialCard}:hover & {
    background: #ffd700;
    transform: translate(-50%, -50%) scale(1.15);
    box-shadow: 0 3px 15px rgba(255, 215, 0, 0.4);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 25px;
    height: 25px;
    
    &::after {
      font-size: 10px;
    }
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.8;
  transition: opacity 0.3s ease;
  
  ${TestimonialCard}:hover & {
    opacity: 1;
  }
`;

const TestimonialInfo = styled.div`
  text-align: center;
`;

const ClientName = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: #ffd700;
  margin-bottom: 3px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 11px;
  }
`;

const ClientDetails = styled.p`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 9px;
  }
`;

const TestimonialQuote = styled.p`
  font-size: 11px;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.85);
  font-style: italic;
  position: relative;
  padding: 0 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &::before {
    content: '"';
    position: absolute;
    left: -3px;
    top: -3px;
    font-size: 16px;
    color: rgba(255, 215, 0, 0.25);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 10px;
    line-height: 1.2;
  }
`;

const Rating = styled.div`
  display: flex;
  justify-content: center;
  gap: 1px;
  margin: 5px 0;
  
  span {
    color: #ffd700;
    font-size: 10px;
  }
`;

// Extract video ID from Vimeo URL and create thumbnail
const getVimeoThumbnail = (videoUrl: string) => {
  const videoId = videoUrl.split('/').pop();
  // Using Vimeo's thumbnail service - you can add #t=5s to get frame at 5 seconds
  return `https://vumbnail.com/${videoId}.jpg`;
};

const testimonials = [
  {
    id: 1,
    name: "Clement",
    title: "Retired Engineer",
    year: "Since 2018",
    quote: "Invaluable retirement guidance.",
    rating: 5,
    videoUrl: "https://player.vimeo.com/video/846822448",
    get thumbnail() { return getVimeoThumbnail(this.videoUrl); }
  },
  {
    id: 2,
    name: "Reuben",
    title: "Business Owner",
    year: "Since 2020",
    quote: "Finally understood annuities.",
    rating: 5,
    videoUrl: "https://player.vimeo.com/video/846822421",
    get thumbnail() { return getVimeoThumbnail(this.videoUrl); }
  },
  {
    id: 3,
    name: "Robert Jackson",
    title: "Retired Executive",
    year: "Since 2019",
    quote: "Right choice for my family.",
    rating: 5,
    videoUrl: "https://player.vimeo.com/video/846822473",
    get thumbnail() { return getVimeoThumbnail(this.videoUrl); }
  },
  {
    id: 4,
    name: "Connie Ward",
    title: "Financial Professional",
    year: "Since 2017",
    quote: "Unmatched expertise.",
    rating: 5,
    videoUrl: "https://player.vimeo.com/video/846819836",
    get thumbnail() { return getVimeoThumbnail(this.videoUrl); }
  },
  {
    id: 5,
    name: "Jeff Jantz",
    title: "Retired Teacher",
    year: "Since 2021",
    quote: "Secured our financial stability.",
    rating: 5,
    videoUrl: "https://player.vimeo.com/video/846819893",
    get thumbnail() { return getVimeoThumbnail(this.videoUrl); }
  }
];

interface VideoTestimonialCarouselProps {
  onTestimonialClick?: (videoUrl: string) => void;
  displayMode?: 'carousel' | 'grid';
}

export const VideoTestimonialCarousel: React.FC<VideoTestimonialCarouselProps> = ({ 
  onTestimonialClick,
  displayMode = 'carousel'
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const handleTestimonialClick = (videoUrl: string) => {
    if (onTestimonialClick) {
      onTestimonialClick(videoUrl);
    } else {
      window.open(videoUrl, '_blank');
    }
  };

  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  if (displayMode === 'grid') {
    return (
      <GridWrapper>
        {testimonials.map((testimonial) => (
          <TestimonialCard 
            key={testimonial.id}
            onClick={() => handleTestimonialClick(testimonial.videoUrl)}
            $isGrid={true}
          >
            <VideoThumbnail>
              <ThumbnailImage 
                src={testimonial.thumbnail} 
                alt={`${testimonial.name} testimonial`}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <PlayButton />
            </VideoThumbnail>
            
            <TestimonialInfo>
              <ClientName>{testimonial.name}</ClientName>
              {/* <ClientDetails>{testimonial.title} • {testimonial.year}</ClientDetails> */}
              
              <Rating>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i}>⭐</span>
                ))}
              </Rating>
              
              {/* <TestimonialQuote>
                {testimonial.quote}
              </TestimonialQuote> */}
            </TestimonialInfo>
          </TestimonialCard>
        ))}
      </GridWrapper>
    );
  }

  return (
    <CarouselWrapper $isGrid={false}>
      <CarouselTrack 
        ref={trackRef}
        $isPaused={isPaused}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <TestimonialCard 
            key={`${testimonial.id}-${index}`}
            onClick={() => handleTestimonialClick(testimonial.videoUrl)}
            $isGrid={false}
          >
            <VideoThumbnail>
              <ThumbnailImage 
                src={testimonial.thumbnail} 
                alt={`${testimonial.name} testimonial`}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <PlayButton />
            </VideoThumbnail>
            
            <TestimonialInfo>
              <ClientName>{testimonial.name}</ClientName>
              {/* <ClientDetails>{testimonial.title} • {testimonial.year}</ClientDetails> */}
              
              <Rating>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i}>⭐</span>
                ))}
              </Rating>
              
              {/* <TestimonialQuote>
                {testimonial.quote}
              </TestimonialQuote> */}
            </TestimonialInfo>
          </TestimonialCard>
        ))}
      </CarouselTrack>
    </CarouselWrapper>
  );
};